<div class="ui-pie-chart" role="figure" [attr.aria-label]="title() || 'Gráfico de pastel'">

  @if (title()) {
    <h3 class="ui-pie-chart__title">{{ title() }}</h3>
  }

  <!-- Canvas + Tooltip wrapper -->
  <div
    class="ui-pie-chart__canvas-wrap"
    (mousemove)="onMouseMove($event)"
    (mouseleave)="tooltipVisible.set(false)"
  >
    @if (loading()) {
      <!-- Skeleton -->
      <div
        class="ui-pie-chart__skeleton"
        [style.width.px]="size()"
        [style.height.px]="svgHeight()"
        aria-hidden="true"
      ></div>
    } @else {
      <svg
        class="ui-pie-chart__svg"
        [attr.width]="svgWidth()"
        [attr.height]="svgHeight()"
        [attr.viewBox]="svgViewBox()"
        aria-hidden="true"
        overflow="visible"
      >
        @if (emptyPlaceholderVisible()) {
          <circle
            class="ui-pie-chart__empty"
            [attr.cx]="cx()"
            [attr.cy]="cy()"
            [attr.r]="outerR()"
          />
        }

        <!-- Segments: outer <g> handles animation, inner <g> handles hover -->
        @for (seg of segments(); track seg.slice.id) {
          <g
            class="ui-pie-chart__anim-shell"
            [class.ui-pie-chart__anim-shell--in]="animated()"
            [style.--seg-delay]="seg.animDelay"
          >
            <!-- Visual: recibe el transform de hover; sin pointer events -->
            <g
              class="ui-pie-chart__segment-group"
              [class.ui-pie-chart__segment-group--hovered]="hoveredId() === seg.slice.id"
              [class.ui-pie-chart__segment-group--active]="activeId() === seg.slice.id"
              [style.transform]="segTransform(seg)"
            >
              <path
                class="ui-pie-chart__segment"
                [attr.d]="seg.path"
                [attr.fill]="seg.color"
                [style.opacity]="segOpacity(seg)"
                [style.stroke]="activeId() === seg.slice.id ? '#fff' : 'none'"
                [style.stroke-width]="activeId() === seg.slice.id ? '2' : '0'"
                style="pointer-events: none;"
              />

              @if (labelMode() === 'inside' && seg.pct > 8) {
                <text
                  class="ui-pie-chart__label ui-pie-chart__label--inside"
                  [attr.x]="seg.insideX"
                  [attr.y]="seg.insideY"
                  text-anchor="middle"
                  dominant-baseline="middle"
                  style="pointer-events: none;"
                >{{ seg.pctLabel }}</text>
              }

              @if (labelMode() === 'outside') {
                <text
                  class="ui-pie-chart__label ui-pie-chart__label--outside"
                  [attr.x]="seg.labelX"
                  [attr.y]="seg.labelY"
                  [attr.text-anchor]="textAnchor(seg)"
                  dominant-baseline="middle"
                  style="pointer-events: none;"
                >{{ seg.slice.label }} {{ seg.pctLabel }}</text>
              }
            </g>

            <!-- Hit area: sin transform, siempre en posición original -->
            <path
              class="ui-pie-chart__hit-area"
              [attr.d]="seg.path"
              fill="transparent"
              stroke="none"
              role="button"
              tabindex="0"
              [attr.aria-label]="seg.slice.label + ': ' + seg.pctLabel"
              (mouseenter)="onSegmentEnter(seg)"
              (mouseleave)="onSegmentLeave()"
              (click)="onSegmentClick(seg)"
              (keydown.enter)="onSegmentClick(seg)"
              (keydown.space)="$event.preventDefault(); onSegmentClick(seg)"
            />
          </g>
        }

        <!-- Donut: texto central -->
        @if (variant() === 'donut' && (centerValue() || centerLabel())) {
          @if (centerValue()) {
            <text
              class="ui-pie-chart__center-value"
              [attr.x]="cx()"
              [attr.y]="centerLabel() ? cy() - 10 : cy() + 5"
              [style.font-size.px]="centerValueFontSize()"
              text-anchor="middle"
              dominant-baseline="middle"
            >{{ centerValue() }}</text>
          }
          @if (centerLabel()) {
            <text
              class="ui-pie-chart__center-label"
              [attr.x]="cx()"
              [attr.y]="centerValue() ? cy() + 15 : cy()"
              text-anchor="middle"
              dominant-baseline="middle"
            >{{ centerLabel() }}</text>
          }
        }

        <!-- Semi: texto debajo del borde plano -->
        @if (variant() === 'semi' && (centerValue() || centerLabel())) {
          @if (centerValue()) {
            <text
              class="ui-pie-chart__center-value"
              [attr.x]="cx()"
              [attr.y]="cy() + 14"
              [style.font-size.px]="centerValueFontSize()"
              text-anchor="middle"
              dominant-baseline="hanging"
            >{{ centerValue() }}</text>
          }
          @if (centerLabel()) {
            <text
              class="ui-pie-chart__center-label ui-pie-chart__center-label--semi"
              [attr.x]="cx()"
              [attr.y]="cy() + (centerValue() ? 40 : 14)"
              text-anchor="middle"
              dominant-baseline="hanging"
            >{{ centerLabel() }}</text>
          }
        }

        <!-- Semi: línea base del gauge -->
        @if (variant() === 'semi') {
          <line
            class="ui-pie-chart__baseline"
            [attr.x1]="cx() - outerR()"
            [attr.y1]="cy()"
            [attr.x2]="cx() + outerR()"
            [attr.y2]="cy()"
          />
        }
      </svg>
    }

    <!-- Tooltip flotante -->
    @if (tooltipVisible() && tooltipSeg()) {
      <div
        class="ui-pie-chart__tooltip"
        [style.left.px]="tooltipX()"
        [style.top.px]="tooltipY()"
        aria-hidden="true"
      >
        <span
          class="ui-pie-chart__tooltip-dot"
          [style.background]="tooltipSeg()!.color"
        ></span>
        <span class="ui-pie-chart__tooltip-body">
          <span class="ui-pie-chart__tooltip-label">{{ tooltipSeg()!.slice.label }}</span>
          <span class="ui-pie-chart__tooltip-row">
            <span class="ui-pie-chart__tooltip-value">{{ legendValue(tooltipSeg()!.slice) }}</span>
            <span class="ui-pie-chart__tooltip-pct">{{ tooltipSeg()!.pctLabel }}</span>
          </span>
        </span>
      </div>
    }
  </div>

  <!-- Leyenda interactiva -->
  @if (showLegend() && !loading() && !emptyPlaceholderVisible()) {
    <ul class="ui-pie-chart__legend" role="list" aria-label="Leyenda del gráfico">
      @for (seg of segments(); track seg.slice.id) {
        <li
          class="ui-pie-chart__legend-item"
          [class.ui-pie-chart__legend-item--hovered]="hoveredId() === seg.slice.id"
          [class.ui-pie-chart__legend-item--active]="activeId() === seg.slice.id"
          (mouseenter)="onLegendEnter(seg)"
          (mouseleave)="onLegendLeave()"
          (click)="onSegmentClick(seg)"
          tabindex="0"
          role="listitem"
          [attr.aria-label]="seg.slice.label + ': ' + legendValue(seg.slice) + ' (' + seg.pctLabel + ')'"
          (keydown.enter)="onSegmentClick(seg)"
        >
          <span
            class="ui-pie-chart__legend-dot"
            [style.background]="seg.color"
            aria-hidden="true"
          ></span>
          <span class="ui-pie-chart__legend-label">{{ seg.slice.label }}</span>
          <span class="ui-pie-chart__legend-value">{{ legendValue(seg.slice) }}</span>
          <span class="ui-pie-chart__legend-pct">{{ seg.pctLabel }}</span>
        </li>
      }
    </ul>
  }
</div>
